import React, { ReactElement, useEffect, useState } from 'react';
import { Space } from 'antd';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { App } from 'common/components/antd/App';
import { Button } from 'common/components/antd/Button/Button';
import { Form } from 'common/components/antd/Form/Form';
import { Input } from 'common/components/antd/Input/Input';
import { authCreateSettingsFlow, authUpdateSettingsFlow } from 'domains/auth/services/auth.service';
import { ROUTES_MAPPING } from 'navigation/constants/route.constants';
import { SCard } from './create-new-password.page.styles';

export const CreateNewPasswordPage = (): ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { notification } = App.useApp();
  const location = useLocation();
  const [isBtnSubmit, setIsBtnSubmit] = useState<boolean>(false);

  useEffect(() => {
    if (!location.state?.isOtpCodeApproved) {
      navigate(ROUTES_MAPPING.RESET_PASSWORD);
    }
  }, []);

  const onSubmitFormHandler = async (values: { newPassword: string }): Promise<void> => {
    try {
      setIsBtnSubmit(true);

      const { newPassword } = values;
      const settingsFlowData = await authCreateSettingsFlow();

      let csrfToken = '';
      const csrfTokenData = settingsFlowData.ui.nodes.find(
        (node) => 'name' in node.attributes && node.attributes.name === 'csrf_token',
      );
      if ('value' in csrfTokenData!.attributes!) {
        csrfToken = csrfTokenData?.attributes?.value;
      }

      const flowId = settingsFlowData.id || '';

      const updateSettingsFlow = await authUpdateSettingsFlow({
        flow: flowId,
        updateSettingsFlowBody: {
          csrf_token: csrfToken,
          password: newPassword,
          method: 'password',
        },
      });

      notification.success({
        message: updateSettingsFlow?.ui?.messages?.[0]?.text,
      });

      navigate(ROUTES_MAPPING.PUBLIC.LOGIN);
    } catch (error) {
      console.error(error);
      setIsBtnSubmit(false);
    }
  };

  const newPasswordValidationRegExp = '^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[@$!%*?_&])[A-Za-z\\d@$!%*?_&]{8,}$';

  return (
    <SCard>
      <Form dataTestId="enter_code_form" onFinish={onSubmitFormHandler}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Form.Item
            name="newPassword"
            hasFeedback
            rules={[
              {
                required: true,
                message: t('auth.createNewPassword.form.validation.required') as string,
              },
              {
                pattern: new RegExp(newPasswordValidationRegExp),
                message: t('auth.createNewPassword.message.errorText') as string,
              },
            ]}
          >
            <Input.Password
              dataTestId="new_password"
              size="large"
              allowClear
              placeholder={t('auth.createNewPassword.input.newPassword')!}
            />
          </Form.Item>
          <Form.Item noStyle>
            <Button
              type="primary"
              dataTestId="submit_button"
              htmlType="submit"
              size="large"
              block
              loading={isBtnSubmit}
            >
              <Trans i18nKey="auth.createNewPassword.button.update" />
            </Button>
          </Form.Item>
        </Space>
      </Form>
    </SCard>
  );
};

export default CreateNewPasswordPage;
